import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom variant picker for the Shop the Look modal.
 * Implements client-side variant resolution to avoid dependency on page fetches.
 */
export default class ShopTheLookVariantPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Initialize variant data
    const script = this.querySelector('script[type="application/json"]');
    if (script) {
        try {
            this.variantData = JSON.parse(script.textContent);
        } catch (e) {
            console.error('Failed to parse variant data', e);
        }
    }
  }

  /**
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    
    // Find the matching variant from JSON
    const currentVariant = this.findCurrentVariant();
    
    if (currentVariant) {
        // Update hidden input
        const hiddenInput = this.querySelector('input[name="id"]');
        if (hiddenInput instanceof HTMLInputElement) {
            hiddenInput.value = currentVariant.id.toString();
        }
        
        // Dispatch events
        // Use a different event name or stop propagation immediately
        // to avoid triggering the main product variant logic
        
        // We create a custom event only for this component or its parent modal
        this.dispatchEvent(new CustomEvent('shop-the-look:variant-selected', { 
            detail: { id: currentVariant.id },
            bubbles: true 
        }));
        
        // Update availability UI (basic)
        this.updateAvailability(currentVariant);
    }

    // Stop event bubbling to prevent main product gallery from updating
    event.stopPropagation();
    event.preventDefault();
  }

  findCurrentVariant() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    const options = fieldsets.map(fieldset => {
        const checked = fieldset.querySelector('input:checked');
        return (checked instanceof HTMLInputElement) ? checked.value : null;
    });
    
    // If dropdowns used instead of buttons
    const selects = Array.from(this.querySelectorAll('select'));
    selects.forEach((select, index) => {
        options[index] = select.value;
    });

    if (!this.variantData) return null;

    /**
     * @param {{options: string[], id: number, available: boolean}} variant
     */
    return this.variantData.find((/** @type {{options: string[], id: number, available: boolean}} */ variant) => {
        return !variant.options.some((/** @type {string} */ option, /** @type {number} */ index) => {
            return options[index] !== null && option !== options[index];
        });
    });
  }
  
  /**
   * @param {{available: boolean}} variant
   */
  updateAvailability(variant) {
      if (!variant.available) {
          this.classList.add('variant-picker--sold-out');
      } else {
          this.classList.remove('variant-picker--sold-out');
      }
  }

  /**
   * @param {HTMLElement} target
   */
  updateSelectedOption(target) {
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (newSelectedOption) {
        for (const option of target.options) option.removeAttribute('selected');
        newSelectedOption.setAttribute('selected', 'selected');
      }
    }
  }
}

if (!customElements.get('shop-the-look-variant-picker')) {
  customElements.define('shop-the-look-variant-picker', ShopTheLookVariantPicker);
}
